import { useContext, useEffect, useState } from "react";
import { Flex, Box, Text } from "rebass/styled-components";
import { ThemeContext } from "styled-components";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";

import Heading from "../common/Heading";
import Icon from "../common/Icon";
import Badge from "../common/Badge";
import Rating from "../common/Rating";
import Button from "../common/Button";
import { useIsMobile } from "../../lib/media";
import { useAuthState } from "../common/AuthContext";
import { AvailablePromotion } from "../../interfaces";
import { UserType } from "../../enums";

interface Props {
  avatar: string;
  title: string;
  name: string;
  address: string;
  city: string;
  reviewScore: number;
  reviewCount: number;
  completedBookingsCount: number;
  repeatCustomersCount: number;
  slug: string;
  availablePromotions?: [AvailablePromotion];
  modalView?: boolean;
  isSuperCaregiver: boolean;
}

export default function Overview(props: Props) {
  const isMobile = useIsMobile();
  const { t } = useTranslation("sitterprofile");
  const { accountMode } = useAuthState();
  const theme = useContext(ThemeContext);

  // Fix Error: Hydration failed because the initial UI
  // does not match what was rendered on the server.
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const redirectToAdmin = () => {
    if (typeof window === "undefined") return;

    const host = window.location.host;
    host &&
      window.location.replace(`https://admin.${host}/caregivers/${props.slug}`);
  };

  return (
    <>
      {isClient && accountMode == UserType.ADMIN && (
        <Flex flexDirection="row-reverse" width={1} p={2}>
          <Button variant="secondary" onClick={redirectToAdmin}>
            {t("viewOnAdmin")}
          </Button>
        </Flex>
      )}

      {isMobile && (
        <Box mt={2} pl={props.modalView ? 0 : 2} width="100%">
          <Text
            as="h1"
            variant="text.h2"
            mb={2}
            sx={{
              fontWeight: "bold",
              marginBottom: 1,
              overflowWrap: "break-word"
            }}
            itemProp="name"
            content={props.title}
          >
            {props.title}
          </Text>
        </Box>
      )}

      <Flex
        justifyContent="flex-start"
        flexWrap={["wrap", "wrap", "nowrap"]}
        ml="auto"
        mr="auto"
        fontSize={3}
        bg="samoyedWhite"
        position="relative"
        sx={{ zIndex: 2 }}
        variant={props.modalView ? undefined : "layout.sitterProfile"}
      >
        <Box pt={[0, 0, 3]} pb={0}>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignSelf="flex-start"
            width={["80px", "152px"]}
          >
            <Box>
              <Box
                width={["80px", "152px"]}
                height={["80px", "152px"]}
                sx={{
                  position: "relative",
                  ".image": { borderRadius: "50%" }
                }}
              >
                <Image
                  src={props.avatar}
                  alt={`${props.name} avatar`}
                  className="image"
                  priority
                  fill
                  sizes="100vw"
                  style={{
                    objectFit: "cover"
                  }}
                />
              </Box>
            </Box>

            <meta itemProp="image" content={`${props.avatar}`} />

            {props.reviewScore && (
              <Box pt="4px">
                <Rating
                  itemProp="aggregateRating"
                  itemScope
                  itemType="http://schema.org/AggregateRating"
                  score={props.reviewScore}
                  sx={{
                    textAlign: "center",
                    justifyContent: "center"
                  }}
                >
                  <meta
                    itemProp="reviewCount"
                    content={`${props.reviewCount}`}
                  />
                  <meta
                    itemProp="ratingValue"
                    content={`${props.reviewScore}`}
                  />
                  <meta itemProp="bestRating" content={`5`} />
                  <meta itemProp="worstRating" content={`1`} />
                </Rating>
              </Box>
            )}
          </Flex>
        </Box>

        <Box pt={[0, 0, 3]} pb={0} pl={[2, 3]} flex={1}>
          {!isMobile && (
            <Text
              as="h1"
              variant="text.h2"
              sx={{
                display: ["none", "block"],
                fontWeight: "bold",
                marginBottom: "1"
              }}
              itemProp="name"
              content={props.title}
            >
              {props.title}
            </Text>
          )}

          <Flex alignItems="center" mb={1}>
            <Heading
              as="p"
              sx={{
                color: "veryDarkGray",
                fontSize: 2,
                fontWeight: "bold"
              }}
            >
              {props.name}
            </Heading>

            {props.isSuperCaregiver && (
              <Box ml={1} mt="5px">
                <Badge primary round>
                  {t("superCaregiver")}
                </Badge>
              </Box>
            )}

            {props.availablePromotions?.map(promotion => (
              <Box ml={1} key={promotion.id}>
                <Badge primary round>
                  {promotion.tLabel}
                </Badge>
              </Box>
            ))}
          </Flex>

          {!!props?.address && (
            <Flex
              mt={1}
              lineHeight="body"
              alignItems="center"
              sx={{ overflow: "hidden", "> svg": { flexShrink: 0 } }}
              fontSize={2}
            >
              <Icon name="marker" color="labradorBlack" />
              <Text
                as="span"
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  ml: ".25px",
                  fontSize: 1
                }}
              >
                {props.address}
              </Text>
            </Flex>
          )}

          {!!props.repeatCustomersCount && (
            <Flex mt={1}>
              <Badge icon="repeat" fontSize={2}>
                {props.repeatCustomersCount}{" "}
                <Trans i18nKey="common:repeatGuests" />
              </Badge>
            </Flex>
          )}

          {props.completedBookingsCount > 0 && (
            <Flex mt={1}>
              <Badge mr={1} fontSize={2}>
                {props.completedBookingsCount}{" "}
                <Trans i18nKey="common:bookings" />
              </Badge>
            </Flex>
          )}

          {props.reviewCount > 0 && (
            <Flex mt={1}>
              <Badge mr={1} fontSize={2}>
                {props.reviewCount} <Trans i18nKey="common:reviews" />
              </Badge>
            </Flex>
          )}
        </Box>

        {isMobile && (
          <>
            {props.isSuperCaregiver && (
              <Flex flexDirection="row" pt={3}>
                <Box mt={"2px"}>
                  <Icon name="medal" fontSize={32} />
                </Box>

                <Text pl={1} flex={1}>
                  <Text as="span" fontWeight="bold">
                    {t("superCaregiverTitle", { name: props.name })}
                  </Text>{" "}
                  <Text as="span">{t("superCaregiverDescription")}</Text>
                </Text>
              </Flex>
            )}

            <Flex flexDirection="row" pt={3}>
              <Box mt={"2px"}>
                <Icon name="bell" fontSize={32} />
              </Box>

              <Text pl={1} flex={1}>
                <Text as="span" fontWeight="bold">
                  {t("urgencyTitle")}
                </Text>{" "}
                <Text as="span">{t("urgencyDescription")}</Text>
              </Text>
            </Flex>
          </>
        )}
      </Flex>
    </>
  );
}
